/**
 * A library card's picture, made when a version is saved (the user's ruling:
 * a real shot, rendered on save) and stored beside the asset.
 *
 * A character's figure is traced art loaded on demand (components/plate
 * /plateArt): it is loaded and fitted here before the picture is drawn. The
 * amorphous mass has no traced art, so its card keeps the kind's drawing.
 *
 * An image's card is the image itself, scaled; a handout (a PDF) keeps the
 * kind's drawing - drawing a PDF's first page would need a PDF renderer.
 *
 * Never fatal: the version is already saved, so a picture that will not draw
 * or store is logged and the card keeps its drawing of the kind.
 */
import { writeThumb } from './assetStore';
import { shotOf } from './engine/assets/kinds';
import { figureLines, THUMB_VIEW, type ThumbShot } from './engine/assets/thumbScene';
import { loadPlateArt, loadVehicleArt } from './components/plate/plateArt';
import type { AssetRow } from './engine/assets/types';
import { ASSET_THUMB_CHARS } from './backend/schema';
import { drawImageThumb, drawThumb } from './utils/thumbCanvas';
import { diag, errorSummary } from './diagnostics/log';

/** The shot with its figure's art fitted in; unchanged without a figure; null for one with no traced art. */
async function withArt(shot: ThumbShot): Promise<ThumbShot | null> {
  const f = shot.figure;
  if (!f) return shot;
  // Neither the mass nor an Object (2026-10-26) has traced art: their thumbnails are drawn, not lined.
  if (f.kind === 'goo' || f.kind === 'object') return null;
  const art = f.kind === 'vehicle' ? await loadVehicleArt(f.vehicle) : await loadPlateArt(f.figure);
  return { ...shot, lines: figureLines(art.art) };
}

export async function storeThumb(uid: string, row: AssetRow, text: string): Promise<void> {
  try {
    const plain = shotOf(row.kind, text);
    const shot = plain && (await withArt(plain));
    const url = shot && (await drawThumb(shot, THUMB_VIEW, ASSET_THUMB_CHARS));
    if (url) await writeThumb(uid, row.id, row.version, url);
  } catch (err) {
    diag.warn('assets', 'the card picture did not save', errorSummary(err));
  }
}

/** A file kind's card: an image scaled to the card; a handout (a PDF) keeps the kind's drawing. */
export async function storeFileThumb(uid: string, row: AssetRow, blob: Blob): Promise<void> {
  if (row.kind !== 'image') return;
  try {
    const url = await drawImageThumb(blob, THUMB_VIEW, ASSET_THUMB_CHARS);
    if (url) await writeThumb(uid, row.id, row.version, url);
  } catch (err) {
    diag.warn('assets', 'the card picture did not save', errorSummary(err));
  }
}
